import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { getStripe, buildStripeAppearance, paymentElementWallets, paymentElementWalletsSuppressed, paymentElementMethodOrder, expressCheckoutPaymentMethods, walletButtonLayout, onWalletStackChange } from '@/api/stripe-loader';
import type { Stripe, StripeElements, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import type { CampaignPromo, OnsiteProduct, OnsitePrice, CheckoutError, CheckoutCompletionResponse, CheckoutSuccessDetails, TrialDiscount } from '@/types';
import { validatePromoCode } from '@/api/client';
import { settleTrialCompletion } from '@/components/trialCompletion';
import { campaignApplyErrorIsSilent, classifyCampaignCode } from '@/campaignPromo';
import { PromoCodeInput, type PromoApplyResult } from '@/components/PromoCodeInput';
import { TestCardHint } from '@/components/TestCardHint';
import { WalletDevNote } from '@/components/WalletDevNote';
import { couponChipLabel, promoReasonMessage, promoDiscountFromValidation } from '@/components/promoDescriptor';
import { lightDomSlotContainer, useStripeElementSlot } from '@/components/useStripeElementSlot';
import { useWalletScrollRestore } from '@/components/useWalletScrollRestore';
import { useEmailVerification } from '@/useEmailVerification';
import { logError, logWarn } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';
import { getStripeLocale } from '@/i18n/loader';
import { confirmRedirect } from '@/stripeConfirmRedirect';

// The calendar date of the first charge: today + the free-trial length. Used in
// the card-required copy so the visitor sees a concrete "won't be charged until"
// date rather than a vague "when it ends".
export function chargeDateLabel(trialPeriodDays?: number): string | null {
  if (!trialPeriodDays || trialPeriodDays <= 0) return null;
  const date = new Date(Date.now() + trialPeriodDays * 24 * 60 * 60 * 1000);
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
  } catch {
    return null;
  }
}

// The trial card screen's subtitle. Shared by TrialPaymentStep (the standalone
// screen) and ExpeditedCheckoutStep (the merged single screen) so the two can't
// drift — the expedited header mirrors the destination screen verbatim.
export function trialSubtitle(trialPeriodDays?: number): string {
  const chargeDate = chargeDateLabel(trialPeriodDays);
  if (!chargeDate) {
    return t('trialPayment.subtitleNoDate');
  }
  return t('trialPayment.subtitle', { date: chargeDate });
}

interface Props {
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // SetupIntent client secret + account: the trial captures a card without
  // charging it (no Checkout Session), so we mount the Payment Element on the
  // SetupIntent and confirm it with stripe.confirmSetup.
  clientSecret: string;
  // Customer Session secret: makes the Payment Element offer the visitor's
  // saved cards. Absent on a deferred start (fresh customer, nothing saved).
  customerSessionClientSecret?: string;
  publishableKey: string;
  connectedAccount?: string;
  // Whether the domain has an active promo code, so the promo entry renders. The
  // trial has no Checkout Session, so an entered code is checked via
  // validatePromoCode and attached at confirm rather than by a session action.
  promotionCodesEnabled?: boolean;
  // Sidebar mount point for the promo entry: it drives the order summary's
  // discount preview, so it belongs with the summary rather than inside the
  // card form. Null while the step's ref hasn't landed; absent renders the
  // entry inline below the card form (standalone embeddings, tests).
  promoTarget?: HTMLElement | null;
  // The visitor's email when the flow knows it raw (guest/expedited paths).
  // Passed to the Payment Element as a billing-details default: prefills any
  // email field and lets Link recognize a returning Link user. Unused in
  // deferred-identity mode (the Link Authentication Element collects it).
  email?: string;
  // Deferred-identity mode (the expedited single screen): the trial was
  // started anonymously (the Phase 1b deferred start), so the form mounts the
  // Link Authentication Element (email) above the Payment Element, gates card
  // confirm on the email element's completeness, and hands the email to
  // onConfirm. Implies the wallet surface unless walletsEnabled is false.
  deferredIdentity?: boolean;
  // Mount the Express Checkout Element in setup mode (Apple Pay / Google Pay /
  // Link) above the card form and suppress the Payment Element's own wallet
  // buttons, so the ECE is the single wallet surface. Deferred-identity mode
  // always has it; the standard step sets this directly (email already known
  // there, so no Link Authentication Element mounts).
  showExpressCheckout?: boolean;
  // False (the domain's wallet switch off) hides every wallet surface: no
  // Express Checkout Element, and the Payment Element's own wallet buttons are
  // suppressed. Absent is treated as true.
  walletsEnabled?: boolean;
  // Creates the trialing subscription on the confirmed SetupIntent's card. The
  // flow owns this (it holds the credential + identifiers); this form hands back
  // the confirmed SetupIntent id, the applied promo code (or null), and — on
  // the deferred-identity path, where no start-time credential exists — the
  // email the visitor entered (or the wallet supplied). A trial-gate
  // ineligibleReason ('trial_card_used' / 'trial_already_redeemed') is a soft
  // denial routed to onTrialIneligible rather than treated as success.
  onConfirm: (setupIntentId: string, promoCode: string | null, email?: string) => Promise<CheckoutCompletionResponse>;
  // Fired when the visitor applies or removes a promo code (null on removal),
  // so the owning flow can attach it to later funnel events.
  onPromoChange?: (code: string | null) => void;
  // Feeds the applied coupon's discount upward so the surrounding step's order
  // summary previews it (the trial has no live session to recompute totals).
  onDiscountChange?: (discount: TrialDiscount | null) => void;
  // Fired when a wallet (Apple Pay / Google Pay / Link) confirm begins. Only
  // the expedited screen wires it: there the wallet confirm is the identity
  // signal (identity_method_selected: wallet). The standard step leaves it
  // unset — a wallet confirm there is purely a payment-method choice.
  onWalletConfirm?: (walletType: string) => void;
  // Fired when the card pay-button submit begins. Only the expedited screen
  // wires it: there the submit is the card path's identity signal
  // (identity_method_selected: guest), symmetric with onWalletConfirm — the
  // Link Authentication Element is complete by the time the button enables.
  // The standard step leaves it unset; identity was already recorded at the
  // gate.
  onCardSubmit?: () => void;
  // Reports submit state so the surrounding step can disable its own chrome
  // (e.g. a Back button) while a confirmation is in flight.
  onSubmittingChange?: (submitting: boolean) => void;
  // Bracket each stripe.confirmSetup call: start fires just before it (with
  // the SetupIntent id and the applied promo code + discount) so the flow can park the
  // selection in case an issuer's 3DS redirect unloads the page; settled fires
  // once it returns or throws, when the in-page flow owns the outcome again.
  onSetupConfirmStart?: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  onSetupConfirmSettled?: (setupIntentId: string) => void;
  // Trial created and provisioned. Carries the backend completion: on the
  // deferred-identity path the widget never held the email, so the success
  // screen reads it off the completion. details carries the applied promo
  // (code + validated discount) so the success screen shows the chip and the
  // discount note — a trial has no session to read them from.
  onSuccess: (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => void;
  // Fired once when the Express Checkout Element reports ready (wallets
  // rendered or determined unavailable). The flows beacon it as the
  // wallets_ready timing marker.
  onWalletsReady?: () => void;
  // Trial-gate soft denial at confirm time: Gate 2 (the card already trialed
  // this product) or Gate 1 (this visitor already trialed — Gate 1 runs only
  // at confirm). Either way the card is already saved, so the flow offers
  // convert-to-paid on it — the id is handed back, along with the applied
  // promo code and its validated discount (or nulls), so the convert re-offers
  // the discount on the paid subscription and the acknowledgment shows it.
  onTrialIneligible: (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => void;
  // The mount's campaign code (URL ?promo= stash or the SDK promoCode
  // option): auto-applied on mount. Failures follow the provenance matrix —
  // campaignApplyErrorIsSilent decides which render inline; the rest open
  // the promo entry with the reason.
  campaignPromo?: CampaignPromo | null;
  // Fired when the campaign code's auto-apply succeeds (the flow beacons
  // promo_campaign_apply).
  onCampaignApply?: () => void;
  // Reports a failed attempt to the publisher. The form stays open for retry, so
  // this may fire more than once.
  onError?: (error: CheckoutError) => void;
}

// TrialPaymentForm is the chrome-free trial card-setup body shared by
// TrialPaymentStep (the standalone trial screen) and ExpeditedCheckoutStep (the
// single-screen guest checkout): it mounts the Payment Element on a trial's
// SetupIntent and owns card confirmation through to the flow's trial creation.
// All surrounding chrome — shell, identity note, back button — belongs to the
// embedding step.
export function TrialPaymentForm({ product, price, clientSecret, customerSessionClientSecret, publishableKey, connectedAccount, promotionCodesEnabled, promoTarget, email, deferredIdentity, showExpressCheckout, walletsEnabled, campaignPromo, onCampaignApply, onConfirm, onPromoChange, onDiscountChange, onWalletConfirm, onWalletsReady, onCardSubmit, onSubmittingChange, onSetupConfirmStart, onSetupConfirmSettled, onSuccess, onTrialIneligible, onError }: Props) {
  useT();
  const slotRef = useRef<HTMLDivElement>(null);
  const walletSlotRef = useRef<HTMLDivElement>(null);
  const contactSlotRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  // Bumped when the Elements instance is created so the email effect re-runs
  // against the ready instance.
  const [elementsReady, setElementsReady] = useState(0);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // atWallet: a wallet attempt failed before Stripe set up the card, so the
  // error renders under the wallet buttons rather than below the unused card form.
  const [error, setError] = useState<{ message: string; atWallet: boolean } | null>(null);
  // The SetupIntent id once the card is confirmed with Stripe. Set so a retry
  // after a backend hiccup re-runs only the trial creation, not card capture.
  const [confirmedSetupIntentId, setConfirmedSetupIntentId] = useState<string | null>(null);
  // The promo code the visitor has applied on this form, and its validated
  // discount. Held here (unlike the paid path, which reads it off the live Stripe
  // session) since the trial has no session; the code is passed to onConfirm so
  // the backend attaches it, and the discount drives the chip + summary preview.
  // Both are handed to the confirm-time denial (onTrialIneligible) so the
  // acknowledgment can show the carried promo. The refs mirror the state for
  // the wallet-confirm listener, which is bound once at element mount and would
  // otherwise close over stale values.
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const appliedPromoCodeRef = useRef<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<TrialDiscount | null>(null);
  const appliedDiscountRef = useRef<TrialDiscount | null>(null);
  // The wallet surface (ECE in setup mode above the card form, PE wallets
  // suppressed) rides with deferred-identity mode and with the standard
  // step's explicit opt-in.
  const walletsOff = walletsEnabled === false;
  const walletSurface = (deferredIdentity || showExpressCheckout) && !walletsOff;
  // Wallet availability drives the wallet block + divider visibility; in
  // deferred-identity mode the Link Authentication Element's completeness and
  // the Payment Element's completeness gate card confirm. The LAE email rides
  // a ref: the wallet-confirm listener reads it and is bound once at mount.
  const [walletsAvailable, setWalletsAvailable] = useState(false);
  // Wallet-slot skeleton: holds the wallet area from first render until the
  // ECE reports ready, so the buttons don't pop in late and shift the card
  // form. Collapses when detection finds no wallets.
  const [walletSkeleton, setWalletSkeleton] = useState(walletSurface);
  const [laeComplete, setLaeComplete] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const laeEmailRef = useRef<string | null>(null);
  // Deliverability gate for the deferred-identity email field (the Link
  // Authentication Element): verify on blur, warn inline. Advisory only.
  const emailVerification = useEmailVerification();
  const restoreWalletScroll = useWalletScrollRestore();

  // The wallet-element confirm listener is bound once at mount; route its
  // callbacks through a ref so it always invokes the latest render's handlers.
  const callbacksRef = useRef({ onWalletConfirm, onWalletsReady, onSetupConfirmStart, onSetupConfirmSettled });
  callbacksRef.current = { onWalletConfirm, onWalletsReady, onSetupConfirmStart, onSetupConfirmSettled };

  // Adopt a discount locally and report it upward in the same place so the chip
  // and the surrounding step's order summary can never drift.
  const applyDiscount = (discount: TrialDiscount | null) => {
    setAppliedDiscount(discount);
    appliedDiscountRef.current = discount;
    onDiscountChange?.(discount);
  };

  // applyPromo validates the entered code against this trial's product/price. On a
  // valid code it shows the applied chip; on an invalid one it returns the reason
  // for the field. The code isn't truly redeemed until confirm attaches it.
  // promoBusy gates the submit button while an apply/remove is in flight, so a
  // confirm inside the validation window can't run without the code the chip is
  // about to show (same race the trial-denial acknowledgment gates).
  const [promoBusy, setPromoBusy] = useState(false);
  // The reason rides failures so the campaign auto-apply can run the
  // provenance matrix; PromoCodeInput only reads the message.
  const applyPromo = async (code: string): Promise<PromoApplyResult & { reason?: string }> => {
    if (!product || !price) {
      return { ok: false, reason: 'apply_failed', message: t('promo.invalid', { code }) };
    }
    setPromoBusy(true);
    try {
      const result = await validatePromoCode({
        productExternalId: product.externalId,
        priceExternalId: price.externalId,
        code,
      });
      if (!result.valid) {
        return { ok: false, reason: result.reason, message: promoReasonMessage(code, result.reason) };
      }
      setAppliedPromoCode(result.code ?? code);
      appliedPromoCodeRef.current = result.code ?? code;
      applyDiscount(promoDiscountFromValidation(result));
      onPromoChange?.(result.code ?? code);
      return { ok: true };
    } finally {
      setPromoBusy(false);
    }
  };

  // Campaign auto-apply: the mount's code validates itself on mount. Silent
  // failures (campaignApplyErrorIsSilent: url provenance + foreign/scope-
  // mismatch reasons) render nothing; every other failure opens the promo
  // entry with the reason.
  const campaignAttemptedRef = useRef(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  useEffect(() => {
    if (!campaignPromo || campaignAttemptedRef.current || appliedPromoCode) {
      return;
    }
    campaignAttemptedRef.current = true;
    const campaign = campaignPromo;
    void (async () => {
      // try/catch: unlike the manual path, nothing wraps this call (see
      // PaymentForm's auto-apply).
      try {
        const result = await applyPromo(campaign.code);
        if (result.ok) {
          classifyCampaignCode(campaign.source, null, 'apply');
          onCampaignApply?.();
          return;
        }
        const reason = result.reason ?? 'apply_failed';
        classifyCampaignCode(campaign.source, reason, 'apply');
        if (!campaignApplyErrorIsSilent(campaign.source, reason)) {
          setCampaignError(result.message ?? t('promo.invalid', { code: campaign.code }));
        }
      } catch (err) {
        classifyCampaignCode(campaign.source, 'apply_failed', 'apply');
        logError('campaign auto-apply failed', err);
        setCampaignError(t('promo.applyFailed', { code: campaign.code }));
      }
    })();
    // applyPromo is re-created per render; the attempt guard is the gate.
  }, [campaignPromo]);

  const removePromo = async (): Promise<PromoApplyResult> => {
    setPromoBusy(true);
    try {
      setAppliedPromoCode(null);
      appliedPromoCodeRef.current = null;
      applyDiscount(null);
      onPromoChange?.(null);
      return { ok: true };
    } finally {
      setPromoBusy(false);
    }
  };

  // Allocate the light-DOM wallet container first (Stripe cannot mount inside
  // the shadow tree). ECE is created in the Payment Element callback below and
  // looked up by slot name — not stashed in a ref, which a stale slot-effect
  // cleanup can null after a remount has already assigned the replacement.
  const walletSlotName = useStripeElementSlot(walletSlotRef, async () => {}, [walletSurface]);

  const slotName = useStripeElementSlot(slotRef, async (mountDiv, cancelled) => {
    let stripe;
    try {
      stripe = await getStripe(publishableKey, connectedAccount);
    } catch (err) {
      logError('failed to load Stripe.js', err);
      if (!cancelled()) {
        setError({ message: err instanceof Error ? err.message : t('paymentForm.loadFailed'), atWallet: false });
        setWalletSkeleton(false);
      }
      return;
    }
    if (cancelled()) return;

    // Raw Elements driven by the SetupIntent client secret (not a Checkout
    // Session): the Payment Element collects a card we confirm with
    // stripe.confirmSetup. Card-only for v1 (the SetupIntent itself is created
    // card-only server-side).
    const elements = stripe.elements({ clientSecret, customerSessionClientSecret, appearance: buildStripeAppearance(slotRef.current), locale: getStripeLocale() });
    stripeRef.current = stripe;
    elementsRef.current = elements;

    // Wallet surface: the Express Checkout Element in setup mode (the Elements
    // instance is bound to the SetupIntent), mounted above the card form.
    // emailRequired makes a wallet without an email on file collect one in the
    // sheet — the deferred trial start has no email until confirm, and on the
    // standard step it keeps the two trial modes uniform.
    const walletDiv = lightDomSlotContainer(walletSlotRef.current, walletSlotName);
    if (walletSurface && !walletDiv) {
      logError('wallet light-DOM container missing before ECE mount');
      setWalletSkeleton(false);
    }
    const expressEl = walletDiv && walletSurface
      ? elements.create('expressCheckout', {
          emailRequired: true,
          paymentMethods: expressCheckoutPaymentMethods,
          layout: walletButtonLayout(),
        })
      : null;
    if (walletSurface && !expressEl) {
      // No wallet element this run — never leave the skeleton up.
      setWalletSkeleton(false);
    }
    if (expressEl && walletDiv) {
      expressEl.on('ready', (event) => {
        if (cancelled()) return;
        setWalletsAvailable(!!event.availablePaymentMethods);
        setWalletSkeleton(false);
        callbacksRef.current.onWalletsReady?.();
      });
      // The wallet iframe failing to load (adblock, CSP, network) never fires
      // ready — drop the skeleton and continue card-only.
      expressEl.on('loaderror', (event) => {
        if (cancelled()) return;
        logWarn('wallet element failed to load', event.error);
        setWalletSkeleton(false);
      });
      expressEl.on('confirm', (event) => {
        void handleWalletConfirm(event);
      });
      try {
        expressEl.mount(walletDiv);
      } catch (err) {
        // A failed wallet mount degrades to card-only; it must not take the
        // Payment Element down with it.
        logError('wallet element mount failed', err);
        setWalletSkeleton(false);
      }
    }
    const offStackChange = expressEl
      ? onWalletStackChange(() => expressEl.update({ layout: walletButtonLayout() }))
      : null;

    // Bump state so the email effect re-runs against the ready Elements
    // instance. The ref is what that effect reads; the state is only the
    // re-run trigger.
    setElementsReady((v) => v + 1);

    const paymentEl = elements.create('payment', {
      layout: 'tabs',
      paymentMethodOrder: paymentElementMethodOrder,
      wallets: walletSurface || walletsOff ? paymentElementWalletsSuppressed : paymentElementWallets(),
      defaultValues: !deferredIdentity && email ? { billingDetails: { email } } : undefined,
    });
    paymentEl.mount(mountDiv);
    if (deferredIdentity) {
      paymentEl.on('change', (event) => {
        if (!cancelled()) setPaymentComplete(event.complete);
      });
    }
    paymentEl.on('ready', () => {
      if (cancelled()) return;
      setReady(true);
      // Land focus on the card field so the flow continues without a click.
      // With the wallet surface on top there is a choice above the card form
      // (deferred mode focuses its email element instead), so leave focus
      // alone.
      if (!walletSurface && !deferredIdentity) {
        paymentEl.focus();
      }
    });

    return () => {
      offStackChange?.();
      expressEl?.destroy();
      paymentEl.destroy();
      stripeRef.current = null;
      elementsRef.current = null;
    };
  }, [clientSecret, customerSessionClientSecret, publishableKey, connectedAccount, email, walletSurface, walletsOff]);

  // Deferred-identity mode: the Link Authentication Element, Stripe's email
  // field for classic Elements. Its change event carries the email, which is
  // sent at ConfirmTrial; it also lands on the PM's billing details at
  // confirmSetup, so receipts/dunning see it.
  const contactSlotName = useStripeElementSlot(contactSlotRef, async (mountDiv, cancelled) => {
    const elements = elementsRef.current;
    if (!deferredIdentity || !elements) {
      return;
    }
    const laeEl = elements.create('linkAuthentication');
    laeEl.on('change', (event) => {
      if (cancelled()) return;
      laeEmailRef.current = event.value.email || null;
      setLaeComplete(event.complete);
      emailVerification.onEmailChange(event.value.email, event.complete);
    });
    // The deliverability verify fires on blur, not per keystroke.
    laeEl.on('blur', () => {
      if (!cancelled()) emailVerification.onEmailBlur();
    });
    laeEl.mount(mountDiv);
    laeEl.on('ready', () => {
      if (cancelled()) return;
      // The email is the first input on the expedited screen.
      laeEl.focus();
    });
    return () => {
      laeEl.destroy();
    };
  }, [elementsReady, deferredIdentity]);

  // Surface a failed attempt both inline (for the visitor) and to the publisher's
  // onError reporter, which the checkout flow has already guarded against throwing.
  // paymentMethod names the tender that failed ('card' or the wallet type) so the
  // funnel's payment_failed can attribute the decline.
  const report = (msg: string, atWallet: boolean, code?: string, paymentMethod?: string) => {
    setError({ message: msg, atWallet });
    onError?.({ message: msg, code, paymentMethod });
  };
  const fail = (msg: string, code?: string, paymentMethod?: string) => report(msg, false, code, paymentMethod);
  const failAtWallet = (msg: string, code?: string, paymentMethod?: string) => report(msg, true, code, paymentMethod);

  const setSubmittingBoth = (value: boolean) => {
    setSubmitting(value);
    onSubmittingChange?.(value);
  };

  // confirmCardSetup runs stripe.confirmSetup bracketed by the park/settle
  // callbacks (see onSetupConfirmStart). allow_redisplay marks the card
  // redisplayable: the trial signup is consent to future billing, so the card
  // surfaces as a saved payment method in the portal and later checkouts.
  // return_url is required because whether a confirm redirects is Stripe's
  // runtime decision, not derivable from our config.
  const confirmCardSetup = async (stripe: Stripe, elements: StripeElements) => {
    const setupIntentId = clientSecret.split('_secret_')[0];
    callbacksRef.current.onSetupConfirmStart?.(setupIntentId, appliedPromoCodeRef.current, appliedDiscountRef.current);
    try {
      return await stripe.confirmSetup({
        elements,
        // 'always' (dev switch only) navigates away and never resolves, so the
        // if_required result shape is the only one this code ever reads.
        redirect: confirmRedirect() as 'if_required',
        confirmParams: { return_url: window.location.href, payment_method_data: { allow_redisplay: 'always' } },
      });
    } finally {
      callbacksRef.current.onSetupConfirmSettled?.(setupIntentId);
    }
  };

  // route applies a completed-shape trial result: success, a trial-gate
  // convert-to-paid handoff, a first invoice that needs on-session
  // authentication (3DS on a setup-fee / SCA-on-create charge), or a
  // still-processing retry.
  const route = async (setupIntentId: string, completion: CheckoutCompletionResponse) => {
    const outcome = await settleTrialCompletion(completion, async () => stripeRef.current);
    if (outcome.kind === 'ineligible') {
      onTrialIneligible(setupIntentId, appliedPromoCodeRef.current, appliedDiscountRef.current);
      return;
    }
    if (outcome.kind === 'failed') {
      fail(outcome.message);
      return;
    }
    // The applied promo rides to the success screen (chip + discount note);
    // read off the refs so the wallet-confirm listener's closure stays fresh.
    const successDetails: CheckoutSuccessDetails = { promoCode: appliedPromoCodeRef.current, discount: appliedDiscountRef.current };
    onSuccess(outcome.completion, successDetails);
  };

  // confirmAndRoute runs the backend trial creation for a confirmed
  // SetupIntent and routes the result. Shared by the card submit and the
  // wallet confirm.
  const confirmAndRoute = async (setupIntentId: string, confirmEmail?: string) => {
    try {
      await route(setupIntentId, await onConfirm(setupIntentId, appliedPromoCodeRef.current, confirmEmail));
    } catch (err) {
      logError('trial confirm failed', err);
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      fail(t('trialPayment.startFailedWithDetail', { detail }));
    }
  };

  // Wallet confirm: confirm the SetupIntent through the ECE confirm event,
  // then create the trial. The email argument matters only on the deferred
  // path (no start-time credential): the wallet-supplied email, falling back
  // to a typed LAE email on the mixed path. On the standard step the flow's
  // start-time credential wins and the email is ignored.
  const handleWalletConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) {
      return;
    }
    callbacksRef.current.onWalletConfirm?.(event.expressPaymentType);
    setSubmittingBoth(true);
    setError(null);

    try {
      // No deliverability gate on the wallet path: the wallet's email is the
      // account-of-record (Apple ID / Google account), not a checkout-typed
      // address, and paid ECE cannot request one. The unreachable-subscriber
      // digest is the backstop for a stale wallet mailbox.
      const result = await confirmCardSetup(stripe, elements);
      if (result.error) {
        event.paymentFailed({ reason: 'fail' });
        failAtWallet(result.error.message ?? t('trialPayment.cardSetupFailed'), result.error.code, event.expressPaymentType);
        return;
      }
      const setupIntent = result.setupIntent;
      if (!setupIntent || setupIntent.status !== 'succeeded' || !setupIntent.id) {
        event.paymentFailed({ reason: 'fail' });
        failAtWallet(t('trialPayment.cardNotSetUp'));
        return;
      }

      restoreWalletScroll(event.expressPaymentType);
      setConfirmedSetupIntentId(setupIntent.id);
      await confirmAndRoute(setupIntent.id, event.billingDetails?.email ?? laeEmailRef.current ?? undefined);
    } catch (err) {
      // Only confirmSetup can throw here: confirmAndRoute reports its own
      // post-setup failures beside the retry button.
      logError('wallet trial setup failed', err);
      event.paymentFailed({ reason: 'fail' });
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      failAtWallet(t('trialPayment.startFailedWithDetail', { detail }));
    } finally {
      setSubmittingBoth(false);
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements || submitting) return;

    onCardSubmit?.();
    setSubmittingBoth(true);
    setError(null);

    try {
      // Card already confirmed on a prior attempt: only the trial creation
      // failed, so retry that rather than re-collecting the card.
      if (confirmedSetupIntentId) {
        await confirmAndRoute(confirmedSetupIntentId, deferredIdentity ? laeEmailRef.current ?? undefined : undefined);
        return;
      }

      const result = await confirmCardSetup(stripe, elements);
      if (result.error) {
        fail(result.error.message ?? t('trialPayment.cardSetupFailed'), result.error.code, 'card');
        return;
      }
      const setupIntent = result.setupIntent;
      if (!setupIntent || setupIntent.status !== 'succeeded' || !setupIntent.id) {
        fail(t('trialPayment.cardNotSetUp'));
        return;
      }

      setConfirmedSetupIntentId(setupIntent.id);
      await confirmAndRoute(setupIntent.id, deferredIdentity ? laeEmailRef.current ?? undefined : undefined);
    } catch (err) {
      logError('trial setup failed', err);
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      fail(t('trialPayment.startFailedWithDetail', { detail }));
    } finally {
      setSubmittingBoth(false);
    }
  };

  // Renders when the domain takes codes — or when a campaign auto-apply
  // failure needs somewhere to show (see PaymentForm's gate).
  const promoBlock = (promotionCodesEnabled || campaignError) && (
    <PromoCodeInput
      onApply={applyPromo}
      onRemove={removePromo}
      appliedCode={appliedPromoCode}
      discountLabel={appliedDiscount && price ? couponChipLabel(price, appliedDiscount) : undefined}
      disabled={submitting}
      externalError={campaignError}
    />
  );

  // The promo entry portals into the step-provided sidebar mount point (it
  // drives the order summary's discount preview, so it belongs with the
  // summary); the validate-and-carry logic stays here — only the rendering
  // moves. A wallet visitor sees it in the sidebar before tapping the modal
  // wallet sheet, whose confirm reads the applied code via ref.
  const renderPromoEntry = () => {
    if (!promoBlock) {
      return null;
    }
    if (promoTarget) {
      return createPortal(promoBlock, promoTarget);
    }
    // Null target: the step's ref hasn't landed yet — render nothing rather
    // than flash the entry inline for a frame.
    return promoTarget === undefined ? promoBlock : null;
  };

  const awaitingCardDetails = !ready || (deferredIdentity && (!laeComplete || !paymentComplete));
  // Without the wallet surface there is no wallet slot; fall back to the form-level spot.
  const walletError = walletSurface && error?.atWallet ? error.message : null;
  const formError = error && !walletError ? error.message : null;

  return (
    <form class="sm-form" onSubmit={handleSubmit}>
      <TestCardHint />
      {walletSurface && (
        <>
          <div ref={walletSlotRef} class={`sm-payment-slot-wallet${!walletsAvailable && !walletSkeleton ? ' sm-payment-slot-wallet--empty' : ''}`}>
            {walletSkeleton && <div class="sm-wallet-skeleton" aria-hidden="true" />}
            <slot name={walletSlotName} />
          </div>
          {walletError && <p class="sm-error" role="alert">{walletError}</p>}
          <WalletDevNote />
          {(walletsAvailable || walletSkeleton) && (
            <div class="sm-wallet-divider" aria-hidden="true"><span>{t('trialPayment.orEnterCard')}</span></div>
          )}
        </>
      )}
      {deferredIdentity && (
        <div ref={contactSlotRef} class="sm-payment-slot-contact"><slot name={contactSlotName} /></div>
      )}
      {deferredIdentity && emailVerification.warnedFor && (
        <p class="sm-error sm-verify-warning" role="alert">{t('emailStep.verifyWarning')}</p>
      )}
      <div ref={slotRef} class="sm-payment-slot"><slot name={slotName} /></div>
      {renderPromoEntry()}

      {formError && <p class="sm-error" role="alert">{formError}</p>}

      <button
        type="submit"
        class="sm-btn sm-btn-primary"
        // After a confirmed setup the button only retries trial creation, so a
        // wallet payer is never held behind the unused card form.
        disabled={submitting || promoBusy || (!confirmedSetupIntentId && awaitingCardDetails)}
      >
        {submitting && <span class="sm-btn-spinner" aria-hidden="true" />}
        {submitting ? t('trialPayment.starting') : confirmedSetupIntentId ? t('paymentForm.checkStatus') : t('trialPayment.startFreeTrial')}
      </button>
    </form>
  );
}
